import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslatePipe} from '@ngx-translate/core';
import {catchError, finalize, forkJoin, Observable, of} from 'rxjs';
import {IFaehigkeit} from '../../shared/model/faehigkeit.model';
import {ISkillCategory, ISkillCategorySaveResponse} from '../../shared/model/skill-management.model';
import {FaehigkeitenService} from '../../services/faehigkeiten.service';
import {MessageService} from '../../shared/components/message.service';
import {compareText, sameText, sortSkills} from './skill-catalog-model';
import {SkillCatalogComponent} from './skill-catalog/skill-catalog.component';
import {SkillDeleteRequest, SkillDetailComponent} from './skill-detail/skill-detail.component';
import {SkillDeleteModalComponent} from './skill-delete-modal/skill-delete-modal.component';
import {SkillCategoriesModalComponent} from './skill-categories-modal/skill-categories-modal.component';
import {SkillAddModalComponent} from './skill-add-modal/skill-add-modal.component';
import {SkillIconComponent} from './skill-icon/skill-icon.component';

type LoadState = 'loading' | 'error' | 'ready';

@Component({
  selector: 'app-skills',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    SkillCatalogComponent,
    SkillDetailComponent,
    SkillDeleteModalComponent,
    SkillCategoriesModalComponent,
    SkillAddModalComponent,
    SkillIconComponent,
  ],
  templateUrl: './skills.component.html',
  styleUrl: './skills.component.scss',
})
export class SkillsComponent implements OnInit {
  private readonly skillService = inject(FaehigkeitenService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(SkillDetailComponent) private detail?: SkillDetailComponent;

  state: LoadState = 'loading';
  skills: IFaehigkeit[] = [];
  categories: ISkillCategory[] = [];
  assignmentCounts = new Map<string, number>();
  selectedId: string | null = null;
  deleteRequest: SkillDeleteRequest | null = null;
  categoriesOpen = false;
  addOpen = false;
  deleting = false;
  private returnFocusTo: HTMLElement | null = null;

  ngOnInit(): void {
    this.load();
  }

  get selectedSkill(): IFaehigkeit | null {
    return this.skills.find((skill) => skill.id === this.selectedId) ?? null;
  }

  get selectedAssignmentCount(): number {
    return this.assignmentCounts.get(this.selectedId ?? '') ?? 0;
  }

  load(): void {
    this.state = 'loading';
    forkJoin({
      skills: this.skillService.queryAll(),
      categories: this.skillService.categories(),
      counts: this.countsOrEmpty(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({skills, categories, counts}) => {
          this.applySkills(skills);
          this.categories = categories;
          this.assignmentCounts = counts;
          this.selectedId = this.selectedSkill?.id ?? this.firstSkillId();
          this.state = 'ready';
          this.cdr.markForCheck();
        },
        error: () => {
          this.state = 'error';
          this.cdr.markForCheck();
        },
      });
  }

  select(id: string): void {
    this.selectedId = id;
  }

  openCategories(): void {
    this.rememberFocus();
    this.categoriesOpen = true;
  }

  closeCategories(): void {
    this.categoriesOpen = false;
    this.restoreFocus();
  }

  onCategoriesSaved(response: ISkillCategorySaveResponse): void {
    this.categories = response.categories;
    this.closeCategories();
    if (response.changed > 0) {
      this.reloadSkills();
    }
  }

  openAdd(): void {
    this.rememberFocus();
    this.addOpen = true;
  }

  closeAdd(): void {
    this.addOpen = false;
    this.restoreFocus();
  }

  onSkillCreated(created: IFaehigkeit): void {
    this.applySkills([...this.skills, created]);
    this.selectedId = created.id ?? this.selectedId;
    this.closeAdd();
    this.reloadCategories();
  }

  onSkillSaved(updated: IFaehigkeit): void {
    const previous = this.skills.find((skill) => skill.id === updated.id);
    this.applySkills(this.skills.map((skill) => (skill.id === updated.id ? updated : skill)));
    if (!sameText(previous?.kategorie, updated.kategorie)) {
      this.reloadCategories();
    }
  }

  onMerged(sourceId: string): void {
    this.applySkills(this.skills.filter((skill) => skill.id !== sourceId));
    this.reloadCounts();
    this.reloadCategories();
  }

  openDelete(request: SkillDeleteRequest): void {
    this.rememberFocus();
    this.deleteRequest = request;
  }

  closeDelete(): void {
    this.deleteRequest = null;
    this.restoreFocus();
  }

  goToSimilar(): void {
    this.deleteRequest = null;
    this.returnFocusTo = null;
    this.cdr.detectChanges();
    this.detail?.scrollToSimilar();
  }

  confirmDelete(): void {
    const skill = this.deleteRequest?.skill;
    if (!skill || this.deleting) {
      return;
    }
    this.deleting = true;
    this.skillService
      .delete(skill.id!)
      .pipe(
        finalize(() => {
          this.deleting = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          const next = this.neighbourInCategory(skill);
          this.applySkills(this.skills.filter((other) => other.id !== skill.id));
          this.selectedId = next?.id ?? this.firstSkillId();
          this.messageService.success('skillManagement.delete.deleted', undefined, {name: skill.faehigkeit});
          this.closeDelete();
          this.reloadCategories();
        },
        error: () => this.messageService.error('skillManagement.delete.deleteError'),
      });
  }

  private applySkills(skills: IFaehigkeit[]): void {
    this.skills = sortSkills(skills);
  }

  private firstSkillId(): string | null {
    const firstSkill = [...this.skills].sort(
      (a, b) => compareText(a.kategorie, b.kategorie) || compareText(a.faehigkeit, b.faehigkeit)
    )[0];
    return firstSkill?.id ?? null;
  }

  private neighbourInCategory(skill: IFaehigkeit): IFaehigkeit | undefined {
    const sameCategory = this.skills.filter((other) => sameText(other.kategorie, skill.kategorie));
    const index = sameCategory.findIndex((other) => other.id === skill.id);
    return sameCategory[index + 1] ?? sameCategory[index - 1];
  }

  private reloadSkills(): void {
    this.skillService
      .queryAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((skills) => {
        this.applySkills(skills);
        this.cdr.markForCheck();
      });
  }

  private reloadCategories(): void {
    this.skillService
      .categories()
      .pipe(
        catchError(() => of(this.categories)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((categories) => {
        this.categories = categories;
        this.cdr.markForCheck();
      });
  }

  private reloadCounts(): void {
    this.countsOrEmpty()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((counts) => {
        this.assignmentCounts = counts;
        this.cdr.markForCheck();
      });
  }

  private countsOrEmpty(): Observable<Map<string, number>> {
    return this.skillService.assignmentCounts().pipe(catchError(() => of(new Map<string, number>())));
  }

  private rememberFocus(): void {
    this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  private restoreFocus(): void {
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target) {
      setTimeout(() => target.isConnected && target.focus());
    }
  }
}
